import { useState, useEffect } from 'react';

export type ImageTone = 'light' | 'dark' | null;

// Returns average perceptual luminance (0–255) of a URL's image, or null while
// loading / on error.  Fetches as a blob so canvas reads are never CORS-tainted.
async function measureBrightness(url: string): Promise<number | null> {
  const res = await fetch(url);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 16;
      canvas.height = 16;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(blobUrl);
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, 16, 16);
      const { data } = ctx.getImageData(0, 0, 16, 16);
      let total = 0;
      let count = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 128) continue; // skip transparent pixels
        total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        count++;
      }
      URL.revokeObjectURL(blobUrl);
      resolve(count > 0 ? total / count : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(null);
    };
    img.src = blobUrl;
  });
}

// Returns "light", "dark", or null (unknown / still loading).
export default function useImageBrightness(url: string | null): ImageTone {
  const [tone, setTone] = useState<ImageTone>(null);

  useEffect(() => {
    if (!url) { setTone(null); return; }
    let cancelled = false;
    measureBrightness(url)
      .then((brightness) => {
        if (cancelled || brightness === null) return;
        setTone(brightness > 200 ? 'light' : brightness < 80 ? 'dark' : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);

  return tone;
}
